import { Link, useNavigate } from "react-router-dom";
import { Bell, Globe, LogOut, ChevronRight, Shield, FileText, Map } from "lucide-react";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";

export default function Profile() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="px-6 md:px-12 pt-7 md:pt-12 mx-auto max-w-3xl" data-testid="profile-page">
      <div className="flex items-center justify-between md:hidden">
        <Logo />
        <LanguageSwitcher />
      </div>

      <div className="mt-10 text-center">
        <div className="inline-flex w-16 h-16 rounded-full bg-brand-brown text-brand-cream items-center justify-center font-serif text-2xl">
          {user?.name?.[0]?.toUpperCase() || "A"}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl text-brand-brown mt-4 leading-tight">
          {user?.name}
        </h1>
        <p className="text-xs tracking-[0.25em] uppercase text-brand-tan mt-1">
          {user?.email}
        </p>
        <p className="text-[10px] tracking-[0.3em] uppercase text-brand-tan/70 mt-3">
          {t("member_since")}{" "}
          {user?.created_at
            ? new Date(user.created_at).toLocaleDateString()
            : "—"}
        </p>
      </div>

      <ul className="mt-10 space-y-3">
        <Row
          to="/profile/notifications"
          icon={Bell}
          label={t("notifications")}
          testid="row-notifications"
        />
        <RowStatic
          icon={Globe}
          label={t("language")}
          right={<LanguageSwitcher />}
          testid="row-language"
        />
        <Row to="/legal/privacy" icon={Shield} label={t("privacy")} testid="row-privacy" />
        <Row to="/legal/cookies" icon={FileText} label={t("cookies")} testid="row-cookies" />
        <Row to="/legal/legal-notice" icon={FileText} label={t("legal_notice")} testid="row-legal" />
        <Row to="/legal/terms" icon={FileText} label={t("terms")} testid="row-terms" />
        <Row to="/sitemap" icon={Map} label={t("sitemap")} testid="row-sitemap" />
      </ul>

      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="mt-10 w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl border border-brand-tan/40 text-brand-brown font-bold tracking-[0.18em] uppercase text-xs hover:bg-white transition-colors"
      >
        <LogOut size={14} strokeWidth={1.5} />
        {t("logout")}
      </button>

      <div className="mt-12 text-center pb-6">
        <p className="text-[9px] tracking-[0.45em] uppercase font-bold text-brand-tan/70">
          ARISTEIA · Catalunya · MMXXVI
        </p>
      </div>
    </div>
  );
}

function Row({ to, icon: Icon, label, testid }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="flex items-center justify-between rounded-2xl border border-brand-tan/25 bg-white/60 hover:bg-white p-4 transition-colors"
      >
        <span className="flex items-center gap-3">
          <Icon size={18} strokeWidth={1.5} className="text-brand-brown" />
          <span className="font-bold text-brand-brown text-sm tracking-wide">
            {label}
          </span>
        </span>
        <ChevronRight size={16} className="text-brand-tan" />
      </Link>
    </li>
  );
}

function RowStatic({ icon: Icon, label, right, testid }) {
  return (
    <li
      data-testid={testid}
      className="flex items-center justify-between rounded-2xl border border-brand-tan/25 bg-white/60 p-4"
    >
      <span className="flex items-center gap-3">
        <Icon size={18} strokeWidth={1.5} className="text-brand-brown" />
        <span className="font-bold text-brand-brown text-sm tracking-wide">
          {label}
        </span>
      </span>
      {right}
    </li>
  );
}
